/**
 * Client-side Web Push subscription helpers.
 * Handles permission, PushManager subscribe/unsubscribe, and backend sync.
 */
import { edgeFn } from '@/lib/edgeFn';
import { getAnonUserId } from '@/lib/userIdentity';
import { trackEvent } from '@/lib/analytics';

// ── VAPID helpers ──

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
}

let cachedVapidKey: string | null = null;

export async function getVapidPublicKey(): Promise<string | null> {
  if (cachedVapidKey) return cachedVapidKey;
  try {
    const data = await edgeFn<{ vapidPublicKey: string }>('push-subscribe', {
      body: { action: 'get-vapid-key' },
    });
    cachedVapidKey = data.vapidPublicKey;
    return cachedVapidKey;
  } catch {
    return null;
  }
}

// ── Permission helpers ──

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
}

// ── Subscribe flow ──

export async function subscribeToPush(dockSlugs?: string[]): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    trackEvent('push_permission_denied', {});
    return null;
  }
  trackEvent('push_permission_granted', {});

  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) {
    console.warn('[push] VAPID public key not available');
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey).slice().buffer as ArrayBuffer,
  });

  // Sync to backend
  const subJson = subscription.toJSON();
  try {
    await edgeFn('push-subscribe', {
      body: {
        action: 'subscribe',
        anonUserId: getAnonUserId(),
        endpoint: subJson.endpoint,
        p256dh: subJson.keys?.p256dh,
        auth: subJson.keys?.auth,
        userAgent: navigator.userAgent,
        dockSlugs: dockSlugs || [],
      },
    });
  } catch (err) {
    console.warn('[push] Backend sync failed:', err);
  }

  return subscription;
}

// ── Unsubscribe flow ──

export async function unsubscribeFromPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      try {
        await edgeFn('push-subscribe', {
          body: {
            action: 'unsubscribe',
            anonUserId: getAnonUserId(),
            endpoint,
          },
        });
      } catch {
        // Best effort backend sync
      }
    }
  } catch {
    // Best effort
  }
}

// ── Update dock slugs ──

export async function updatePushDockSlugs(dockSlugs: string[]): Promise<void> {
  try {
    await edgeFn('push-subscribe', {
      body: {
        action: 'update-docks',
        anonUserId: getAnonUserId(),
        dockSlugs,
      },
    });
  } catch {
    // Best effort
  }
}

// ── SW message listener for error forwarding ──

export function installPushSwListener(): void {
  if (!('serviceWorker' in navigator)) return;

  const errorBuckets = new Set<string>();

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'PUSH_SW_ERROR') {
      const msg = String(event.data.message || 'unknown');
      // Dedupe per hour bucket
      const bucket = `${msg.slice(0, 50)}_${Math.floor(Date.now() / 3600000)}`;
      if (!errorBuckets.has(bucket)) {
        errorBuckets.add(bucket);
        trackEvent('push_sw_error', { message: msg.slice(0, 200) });
      }
    }
  });
}
